// Product-tour registry — the single source of truth for in-app guided tours.
//
// Tours live here as typed data, not in a CMS, so anchors, copy, ordering and
// gating are reviewed in the same PR as the UI they point at. A rename that
// breaks a tour anchor is caught by `scripts/check-tour-anchors.mjs`, which runs
// as the first step of `pnpm --filter platform build` — a broken anchor is a
// BUILD failure, not a silent runtime shrug.
//
// Anchors are `data-tour="<id>"` attributes on real surfaces. See
// `context/working-conventions.md` §56 — `data-tour` is an API, not markup.
// A step with no `anchor` renders as a centered card. A step whose anchor is not
// currently in the DOM degrades to a centered card with no spotlight, so a tour
// can never hard-break a page.

import { ROUTES } from 'routes'

export type TourStepPlacement = 'top' | 'bottom' | 'left' | 'right' | 'center'

export interface TourStepCta {
  /** Button label on the terminal step. */
  label: string
  /** An action key the host maps to a handler (dispatched as TOUR_ACTION_EVENT),
   *  OR an in-app route to navigate to. */
  action?: string
  href?: string
}

export interface TourStep {
  id: string
  /** `data-tour` value of the element this step is about. Omit for a centered step. */
  anchor?: string
  title: string
  body: string
  placement?: TourStepPlacement
  /** Terminal action rendered as the primary button on this step. */
  cta?: TourStepCta
  /** Action the host runs WHEN THIS STEP BECOMES ACTIVE (not on a click), so the
   *  surface the step describes is actually present — open a modal, switch a tab.
   *  Dispatched as TOUR_ACTION_EVENT; surfaces listen. Without it, a step that
   *  describes a modal shows its card over whatever happens to be on screen. */
  onEnter?: string
  /** Interactive "do it" step. Spotlights the REAL `anchor` and invites the user
   *  to click it — clicking the real element fires its own handler AND advances
   *  the tour, so the user learns the gesture. If they click the step's button
   *  instead, the host runs `action` (doing it for them) and then advances.
   *  Requires `anchor`. Distinct from `onEnter`, which fires unprompted. */
  awaitAction?: { action?: string; buttonLabel?: string }
  /** Branch buttons on a terminal step — a "where next?" hub that launches
   *  another tour by id, so a short tour can hand off instead of growing into
   *  one long linear walkthrough. */
  branches?: { label: string; tourId: string }[]
  /** Override which element gets the spotlight ring. `anchor` still positions
   *  the card. Use when the card should sit against a large container (a modal)
   *  but the ring should call out one control inside it. */
  spotlight?: string
}

/** Grouping bucket for the tour list. Keep this list short — a handful of
 *  legible buckets beats a long flat list. */
export type TourCategory =
  | 'Getting started'
  | 'Certificates & products'
  | 'Tags & orders'
  | 'Settings & access'
  | 'People & owners'

export interface TourDef {
  id: string
  /** Label shown in the tour list. */
  label: string
  /** One-line description shown under the label. */
  description: string
  category: TourCategory
  /**
   * The one anchor that decides whether this tour is relevant to the current
   * route ("On this page" in the tour list). Optional; falls back to "any anchor
   * resolves".
   *
   * It exists because most tours touch global chrome — the sidebar, the help
   * row — and "any anchor resolves" would then mark every tour as on-page
   * everywhere, which is the same as having no filter at all. Point this at the
   * one element that only exists on the tour's own surface.
   */
  scopeAnchor?: string
  /**
   * This tour spotlights RECORDS, so it is meaningless in an empty console.
   *
   * VRG-512. When set, an explicit launch on a tenant with no sample data offers to
   * add it first — which is the second of the two entry points to intro data, and the
   * reason the materialize endpoint has to be idempotent (it can fire on every launch).
   *
   * Set it only for tours that walk records the intro set ACTUALLY SEEDS — today that
   * is products and certificates. Deliberate omissions, each for its own reason:
   *
   *   `people-and-owners`        v1 seeds no owners (the STI shared-id model makes one
   *                              a three-table write — see `intro-content.ts`), so
   *                              offering samples would not populate its surface.
   *   `ask-vera-basics`          Ask EXCLUDES seeded rows by design, so sample data
   *                              would change nothing it can answer.
   *   `certificate-detail`       record-scoped, no `route` — only offered while you
   *   `product-edit-navigation`  are already standing on a record or a create form.
   *   `creating-certificates`    teaches how to make the first one; an empty console
   *                              is its natural starting state.
   *   chrome tours               `settings-orientation`, `how-to-help-yourself`,
   *                              `browsing-guides` read fine when empty, and offering
   *                              to write rows first would be an unprompted write
   *                              dressed up as a prerequisite.
   *
   * ⚠️ Interacts with §56. Anchoring a tour to seeded rows makes intro data a tour
   * DEPENDENCY: clearing the samples can break a step, and `check-tour-anchors.mjs`
   * will not catch it because the anchor still exists in source.
   */
  requiresIntroData?: boolean
  /**
   * The route this tour runs ON. Launching it from anywhere else navigates here FIRST.
   *
   * Without this, every tour assumed the user was already standing on its surface: the engine
   * only ever navigated on a terminal `cta.href`, so starting `roles-and-permissions` from the
   * tour list while sitting on Home looked for `data-tour` anchors that exist only on
   * /settings/roles, found none, and attached to nothing. That was true of nearly every tour
   * in the dialog.
   *
   * OPTIONAL, and the omissions are deliberate rather than unfinished: a tour anchored to a
   * RECORD page (a specific certificate, a specific role, an existing product's edit form) has
   * no static route to send anyone to. Those stay start-where-you-are and are only offered
   * from the record itself — which is what `scopeAnchor` already gates in the list.
   */
  route?: string
  steps: TourStep[]
}

// ─── Anchor ids — the contract with the UI surfaces ────────────────────────
// Every value here must appear as a `data-tour="<value>"` attribute on a real
// surface, or the build fails. Referencing an anchor via this map (rather than a
// bare string) is what lets the checker name the tours a missing anchor breaks.
export const TOUR_ANCHORS = {
  /** The sidebar's scrollable nav list. */
  sidebarNav: 'sidebar-nav',
  /** The sidebar's Help row (opens the help popover, which lists the tours). */
  sidebarHelp: 'sidebar-help',
  /** The persistent Ask Vera launcher in the page `Header` — present on every
   *  non-nested page (VRG-227), unlike every other anchor in this registry. */
  askVeraLauncher: 'ask-vera-launcher',
  /** The certificates list — the shared Table's outer box on /certificates. */
  certificatesTable: 'certificates-table',
  /** "Create Certificates" — the primary action in the certificates header. */
  certificatesCreate: 'certificates-create',
  /** "Transfer" — the secondary action in the certificates header. */
  certificatesTransfer: 'certificates-transfer',

  // ── Create Certificates modal (CreateCertificatesModal.tsx) ──────────────
  /** The product picker inside the create-certificates flow. */
  createCertificatesProductPicker: 'create-certificates-product-picker',
  /** The live NFC scan session that tallies scanned tags. */
  createCertificatesScanSession: 'create-certificates-scan-session',
  /** The "scan a package handle" bulk shortcut. */
  createCertificatesPackageHandle: 'create-certificates-package-handle',
  /** The "Create Certificates" submit button. */
  createCertificatesSubmit: 'create-certificates-submit',

  // ── Certificate detail page (CertificatePage.tsx) ─────────────────────────
  /** The record's action kebab, which holds Transfer among other actions. */
  certificateDetailTransfer: 'certificate-detail-transfer',
  /** The Provenance tab — the certificate's chain-of-custody history. */
  certificateDetailProvenance: 'certificate-detail-provenance',
  /** The Activity tab — the certificate's scan/event timeline. */
  certificateDetailActivity: 'certificate-detail-activity',

  // ── Products (ProductsPage.tsx / ProductEditPage.tsx) ─────────────────────
  /** The products list — the shared Table's outer box on /products. */
  productsTable: 'products-table',
  /** "Add Products" — the products list's primary header action. */
  productsCreate: 'products-create',
  /** The product edit form's left scroll-spy section outline (§30). */
  productEditToc: 'product-edit-toc',
  /** The product edit form's field search/filter box. */
  productEditSearch: 'product-edit-search',

  // ── Installation (InstallationPage.tsx) ───────────────────────────────────
  /** The "Where scans go" destination picker. */
  installationScanDestination: 'installation-scan-destination',
  /** The Vera subdomain view/edit control. */
  installationSubdomain: 'installation-subdomain',
  /** The embed/widget snippet copy button. */
  installationWidgetSnippet: 'installation-widget-snippet',

  // ── Billing & Features (BillingPage.tsx / FeaturesPage.tsx) ───────────────
  /** The Billing overview tab — plan, next invoice, activation usage. */
  billingOverview: 'billing-overview',
  /** "Add payment method" on the Payment methods tab. */
  billingAddPaymentMethod: 'billing-add-payment-method',
  /** The "Certificate activation triggers" tile row on Features. */
  featuresActivationTriggers: 'features-activation-triggers',

  // ── Roles & permissions (RolesPage.tsx / RolePage.tsx) ────────────────────
  /** The permission matrix on the Roles page's Permissions tab. */
  rolesMatrix: 'roles-matrix',
  /** "Assign users" on a single role's detail page. */
  roleAssignUsers: 'role-assign-users',

  // ── Settings shell (SettingsPage.tsx) ──────────────────────────────────────
  /** The Personal/Company sectioned sub-nav rail. */
  settingsNav: 'settings-nav',

  // ── People & owners (PeoplePage.tsx / OwnerPage.tsx) ──────────────────────
  /** The owners list — the shared Table's outer box on /people. */
  peopleTable: 'people-table',
  /** A single owner's record — their currently- and previously-owned certificates. */
  ownerDetail: 'owner-detail',

  // ── Studio (StudioPage.tsx and its ThemeForm / LogoUploadCard) ────────────
  /** The brand color/font/radius editor card. */
  studioThemeForm: 'studio-theme-form',
  /** The logo upload card. */
  studioLogoUpload: 'studio-logo-upload',
  /** The Certificate/Email template tab row. */
  studioTemplateTabs: 'studio-template-tabs',

  // ── Self-service (SupportPage.tsx / DocsLayout.tsx / GlossaryPage.tsx / WhatsNewPage.tsx) ──
  /** The support-request list (personal "My requests" or company "Support"). */
  supportRequests: 'support-requests',
  /** "New request" on the Support page. */
  supportNewRequest: 'support-new-request',
  /** The docs reader's left section-nav column. */
  docsNav: 'docs-nav',
  /** The glossary's term list. */
  glossaryList: 'glossary-list',
  /**
   * The changelog's FIRST entry card — not the feed that contains it.
   *
   * It was the feed (`whats-new-list`, the `<Stack>` wrapping every card) and that
   * could never be spotlighted: its height is `n × card` and grows with the
   * changelog, measuring 976 × 1749.5 against an 800px viewport, so `canSpotlight`
   * correctly refused to ring it and the step degraded to a flat dim on every screen
   * anyone owns. A ring around the whole content area says nothing anyway — "this,
   * not that" needs a "not that".
   *
   * One card is bounded, and it is what the step's copy actually describes. The
   * PRESENCE CONDITION is unchanged: the old anchor also lived inside the
   * `announcements.length > 0` branch, so an empty archive had no anchor before this
   * and has none after. That is deliberate — with nothing shipped there is nothing to
   * point at, and the tour drops out of "On this page" rather than ringing an empty
   * state (see `whatsNewTour` for what the tour library still does with it).
   */
  whatsNewEntry: 'whats-new-entry',
} as const

// ─── The catalog ───────────────────────────────────────────────────────────

/**
 * Ask Vera (VRG-227) — the panel's own orientation tour.
 *
 * Deliberately two real steps, not a walkthrough of the panel's internals: every
 * surface INSIDE the panel (the composer, the message list, suggested questions)
 * belongs to a different in-flight change this tour must not race, so the only
 * anchor available here is the launcher itself. That is also the one idea worth
 * teaching up front — this is not a help search, it answers questions against the
 * brand's OWN data, and it is reachable from anywhere, not just ⌘K.
 *
 * No `route` and no `scopeAnchor` beyond the launcher — the anchor exists on every
 * non-nested page (`Header` renders it unconditionally), so there is nothing to
 * navigate to and nothing that makes this tour more "on this page" on one route
 * than another.
 */
const askVeraBasics: TourDef = {
  id: 'ask-vera-basics',
  label: 'Ask Vera',
  description: 'What it answers, and how to reach it from anywhere.',
  category: 'Getting started',
  scopeAnchor: TOUR_ANCHORS.askVeraLauncher,
  steps: [
    {
      id: 'welcome',
      title: 'Ask Vera about your own data',
      body: "Not a help search — Ask Vera answers using your company's actual certificates, products, scans and owners.",
      placement: 'center',
    },
    {
      id: 'launcher',
      anchor: TOUR_ANCHORS.askVeraLauncher,
      title: 'Open it from anywhere',
      body: 'This button sits at the top of every page. Click it, or press ⌘K, whenever you have a question about your data.',
      placement: 'bottom',
      awaitAction: { buttonLabel: 'Open Ask Vera' },
    },
  ],
}

/**
 * The exemplar tour (VRG-228). One tour, on a high-traffic, structurally stable
 * surface — the certificates list, which every brand user lands on — chosen so
 * the anchors are unlikely to move out from under it while the tour library is
 * still being written.
 *
 * It exercises the engine end to end: a centered opener with no anchor, three
 * anchored steps with different placements, and a terminal `awaitAction` step
 * whose real element (the Help row) both advances the tour and does the thing
 * the step is teaching.
 */
const certificatesBasics: TourDef = {
  id: 'certificates-basics',
  route: ROUTES.CERTIFICATES,
  // Every step points at a ROW in the certificates table. On an empty console the
  // table is a zero state and the tour narrates nothing (VRG-512).
  requiresIntroData: true,
  label: 'Certificates: the basics',
  description: 'Where certificates live, how to read the list, and how to issue or transfer one.',
  category: 'Getting started',
  // The certificates table only exists on /certificates — unlike the sidebar
  // anchors this tour also uses, which are present on every route.
  scopeAnchor: TOUR_ANCHORS.certificatesTable,
  steps: [
    {
      id: 'welcome',
      title: 'A quick tour of Certificates',
      body: 'Five steps, about a minute. Arrow keys move between steps and Esc leaves at any point.',
      placement: 'center',
    },
    {
      id: 'nav',
      anchor: TOUR_ANCHORS.sidebarNav,
      title: 'Everything hangs off the sidebar',
      body: 'Certificates, Products, Tags, People and Analytics each get their own list. Certificates is where a physical item and its digital record meet.',
      placement: 'right',
    },
    {
      id: 'list',
      anchor: TOUR_ANCHORS.certificatesTable,
      title: 'The certificate list',
      body: 'One row per certificate: which product it represents, its status, who owns it now, and how many times it has been scanned. Search, filter and column controls sit above the table.',
      placement: 'top',
    },
    {
      id: 'create',
      anchor: TOUR_ANCHORS.certificatesCreate,
      title: 'Issuing certificates',
      body: 'Create Certificates issues them against a product, one at a time or in bulk. Each one gets encoded onto a tag before it goes out.',
      placement: 'bottom',
    },
    {
      id: 'transfer',
      anchor: TOUR_ANCHORS.certificatesTransfer,
      title: 'Handing over ownership',
      body: 'Transfer moves a certificate to a new owner. The previous owner stays in the provenance record, so the chain of custody is never overwritten.',
      placement: 'bottom',
    },
  ],
}

/**
 * Creating certificates — a deeper look at the single modal behind "Create
 * Certificates", authored because bulk vs. single and the package-handle
 * shortcut are easy to miss on a first pass (working-conventions.md's own
 * VRG-228 candidate-tour notes flagged this).
 */
const creatingCertificates: TourDef = {
  id: 'creating-certificates-deep-dive',
  route: ROUTES.CERTIFICATES,
  label: 'Creating certificates',
  description: 'The live scan session, bulk creation, and the package-handle shortcut.',
  category: 'Certificates & products',
  scopeAnchor: TOUR_ANCHORS.certificatesCreate,
  steps: [
    {
      id: 'welcome',
      title: 'A closer look at Create Certificates',
      body: "It's one modal, not a multi-step wizard — here's what's actually happening inside it.",
      placement: 'center',
    },
    {
      id: 'open',
      anchor: TOUR_ANCHORS.certificatesCreate,
      title: 'Open Create Certificates',
      body: 'Pick a product, then scan tags to create one certificate per tag.',
      placement: 'bottom',
      awaitAction: { buttonLabel: 'Open Create Certificates' },
    },
    {
      id: 'product',
      anchor: TOUR_ANCHORS.createCertificatesProductPicker,
      title: 'Pick a product',
      body: 'Search and select one now — every certificate created in this session gets issued against it, and the rest of this tour needs a product chosen to keep going.',
      placement: 'bottom',
    },
    {
      id: 'scan',
      anchor: TOUR_ANCHORS.createCertificatesScanSession,
      title: 'The live scan session',
      body: "Hold each tag near your reader. Every scan tallies here — one tag makes one certificate, and there's no separate 'bulk mode': it's just how many tags you scan in this session.",
      placement: 'top',
    },
    {
      id: 'package',
      anchor: TOUR_ANCHORS.createCertificatesPackageHandle,
      title: 'Shipping a whole package?',
      body: "Scan a package's handle tag instead, and every tag inside it resolves and gets added automatically — no need to scan each one by hand.",
      placement: 'top',
    },
    {
      id: 'submit',
      anchor: TOUR_ANCHORS.createCertificatesSubmit,
      title: 'Create Certificates',
      body: 'This creates one certificate per scanned tag, encoded and ready to ship.',
      placement: 'top',
    },
  ],
}

/**
 * Certificate groups (VRG-231) — divisible vs indivisible, and the three things
 * that choice decides: how the group activates, what it costs, and whether it can
 * ever be taken apart.
 *
 * ## Why ONE tour and not two
 *
 * "What a group is / how to make one" and "what happens when you activate or split
 * one" look like two jobs, and a second tour could have lived on a group parent's
 * detail page (route-less, scoped to its own anchor — the `certificateDetailTour` /
 * `assigningRoleUsers` pattern). It is still one tour, because the content is ONE
 * causal chain and cutting it puts a rule and its consequence on different surfaces:
 * divisibility is the pivot, and the activation-cascade, the billing, and the
 * split-lock are all downstream of it. Splitting the tour would mean a user learns
 * that indivisible can never be un-made AFTER they have already chosen it, which is
 * §40's "a caveat at the bottom is read after the decision" in tour form.
 *
 * ## Why almost every step is a centered card, and why that is not a defect here
 *
 * Groups have essentially NO stable DOM to spotlight. Every group control is
 * transient or conditional: "Group certificates" exists only once >= 2 rows are
 * ticked, "Create group" / "Split group" / "Remove from group" live inside a row
 * kebab that must be opened first, the divisibility picker lives inside a modal,
 * `ExpandAllGroupsToggle` renders `null` unless the page already contains a group,
 * and the detail page's "Children" section needs both a group parent and a record
 * route. Anchoring any of those would ship a `data-tour` contract (§56) that
 * resolves almost never and degrades to a flat card whose copy describes a ring
 * nobody can see.
 *
 * So the only anchor used is `certificatesTable`, on the one step whose claim is
 * actually about the list. The other four steps teach RULES, not controls, and a
 * rule's honest presentation is a centered card — the same shape
 * `billingActivations`'s "What actually triggers billing" step already takes.
 *
 * What would fix it: anchors inside the create-group modal, reachable only once the
 * engine can OPEN a modal on the user's behalf. `onEnter` exists in `TourStep` for
 * exactly that, but no surface in the app listens for `TOUR_ACTION_EVENT` today, so
 * an action key dispatches into the void. Same reason there is no `awaitAction`
 * here: it requires an `anchor`, and its whole value is that clicking the REAL
 * element both does the thing and advances — which needs a real element.
 *
 * ## A brand with NO groups yet is the target reader, so nothing may depend on one
 *
 * `scopeAnchor` is `certificatesTable`, deliberately NOT a group-conditional
 * element. Gating this tour's "on this page" on already having a group would hide
 * it from exactly the person it is written for. (Contrast `whatsNewTour`, which
 * correctly drops out on an empty archive — there the tour's subject IS the absent
 * content; here the subject is a capability, which exists whether or not it has
 * been used.) The `list` step is written as "what to look for", true with zero
 * groups on screen; every other step is a rule or an entry point, equally true.
 */
const certificateGroups: TourDef = {
  id: 'certificate-groups',
  route: ROUTES.CERTIFICATES,
  // Divisible-vs-indivisible is only legible against actual certificates.
  requiresIntroData: true,
  label: 'Certificate groups',
  description: 'Divisible vs indivisible, and what that choice does to activation, billing and splitting.',
  category: 'Certificates & products',
  scopeAnchor: TOUR_ANCHORS.certificatesTable,
  steps: [
    {
      id: 'welcome',
      title: 'When one item has several tags',
      body: 'A group ties several tagged certificates together as one item. Every member keeps its own product, its own history and its own tag. The group itself carries no tag.',
      placement: 'center',
    },
    {
      id: 'list',
      anchor: TOUR_ANCHORS.certificatesTable,
      title: 'Groups live in this list',
      body: 'A group appears as one row with a member count, which expands to show the members as ordinary rows. To make one, tick two or more rows and choose Group certificates from the bar that appears, or open a single row\'s menu and choose Create group.',
      placement: 'top',
    },
    {
      id: 'divisibility',
      title: 'Divisible or indivisible',
      body: 'This is a commercial decision, not a technical one. Divisible means the pieces can be sold separately. Indivisible means the item is only ever sold whole. The next three steps all follow from it.',
      placement: 'center',
    },
    {
      id: 'activation',
      title: 'What it costs to activate',
      body: 'Activating any member of an indivisible group activates every member, and the whole group is charged as one activation. A divisible group has no such link. Each member activates on its own and is billed on its own.',
      placement: 'center',
    },
    {
      id: 'split',
      title: 'Splitting and removing are different',
      body: "Split group detaches every member at once, and the group's tagless parent is archived. Remove from group takes out one member and leaves the rest grouped. Removing the last member dissolves the group and archives the parent.",
      placement: 'center',
    },
    {
      id: 'lock',
      title: 'Indivisible is permanent once it is live',
      body: 'You can still split an indivisible group while every member is inactive and unowned, which is there to let you correct a mislabel. Once any member goes live or is owned, that door closes for good and the split actions stop appearing.',
      placement: 'center',
    },
  ],
}

/**
 * Certificate detail — provenance, activity and transfer, all on the single
 * certificate record. Originally scoped as two candidates ("Transferring
 * certificates" and "Certificate detail"); merged into one tour because all
 * three anchors live on the same small page and splitting them would have
 * meant two tours with the same `scopeAnchor` competing for "on this page."
 */
const certificateDetailTour: TourDef = {
  id: 'certificate-detail',
  label: 'Certificate detail: provenance, activity & transfer',
  description: "Where a certificate's own history lives, and how to hand it to someone else.",
  category: 'Certificates & products',
  scopeAnchor: TOUR_ANCHORS.certificateDetailProvenance,
  steps: [
    {
      id: 'welcome',
      title: "A certificate's own record",
      body: "The list page has a tour already — this one is about what's on a single certificate's detail page.",
      placement: 'center',
    },
    {
      id: 'provenance',
      anchor: TOUR_ANCHORS.certificateDetailProvenance,
      title: 'Provenance',
      // VERA-931 — was "who's owned it, in order. Transferring ownership adds to
      // this list". Both halves became incomplete when stores joined the chain: a
      // store is a place the item passed THROUGH while the brand still owned it, so
      // it is not an owner and it did not arrive here via a transfer.
      body: "Every certificate keeps its full chain of custody here — everyone who has owned it, plus any of your stores it passed through, in order. Transfers and store arrivals both add to this list; neither overwrites what came before.",
      placement: 'top',
    },
    {
      id: 'activity',
      anchor: TOUR_ANCHORS.certificateDetailActivity,
      title: 'Activity',
      body: 'Every scan and event against this certificate, in one timeline.',
      placement: 'top',
    },
    {
      id: 'transfer',
      anchor: TOUR_ANCHORS.certificateDetailTransfer,
      title: 'Handing over ownership',
      body: "Transfer lives in this menu alongside the record's other actions. It moves ownership and adds a new entry to Provenance — the previous owner is never erased.",
      placement: 'bottom',
      awaitAction: { buttonLabel: 'Open actions' },
    },
  ],
}

/**
 * Products catalog basics — the list and "Add Products," plus a terminal
 * pointer into the edit form's own scroll-spy TOC (§30), which can't be
 * anchored directly here since it lives on a different route.
 */
const productsCatalogBasics: TourDef = {
  id: 'products-catalog-basics',
  route: ROUTES.PRODUCTS,
  // Walks the product LIST; an empty catalogue has no list to walk.
  requiresIntroData: true,
  label: 'Products: catalog basics',
  description: 'The product list, adding new products, and finding your way around the edit form.',
  category: 'Certificates & products',
  scopeAnchor: TOUR_ANCHORS.productsTable,
  steps: [
    {
      id: 'welcome',
      title: 'Your product catalog',
      body: 'Where products live — what certificates get issued against.',
      placement: 'center',
    },
    {
      id: 'table',
      anchor: TOUR_ANCHORS.productsTable,
      title: 'The product list',
      body: 'One row per product.',
      placement: 'top',
    },
    {
      id: 'create',
      anchor: TOUR_ANCHORS.productsCreate,
      title: 'Add Products',
      body: 'Search your existing catalog to import a product, or create a brand-new one from scratch.',
      placement: 'bottom',
    },
    {
      id: 'edit-form',
      title: 'Building out a product page',
      body: "As a product's form grows, a left-hand section outline and a search box help you jump straight to any field instead of scrolling to find it.",
      placement: 'center',
      cta: { label: 'Create a product', href: '/products/create' },
    },
  ],
}

/**
 * Satellite of `productsCatalogBasics` — the edit form's own scroll-spy TOC
 * and search box (§30), on the product edit route rather than the list.
 */
const productEditNavigation: TourDef = {
  id: 'product-edit-navigation',
  route: ROUTES.PRODUCT_CREATE,
  label: 'Finding your way around a product form',
  description: 'The section outline and search box on a growing product form.',
  category: 'Certificates & products',
  scopeAnchor: TOUR_ANCHORS.productEditToc,
  steps: [
    {
      id: 'welcome',
      title: 'A form that grows',
      body: 'As a product picks up more fields, this page grows with it — here\'s how to get around.',
      placement: 'center',
    },
    {
      id: 'toc',
      anchor: TOUR_ANCHORS.productEditToc,
      title: 'Jump to any section',
      body: 'This outline tracks your scroll position and lets you jump straight to any section.',
      placement: 'right',
    },
    {
      id: 'search',
      anchor: TOUR_ANCHORS.productEditSearch,
      title: 'Or just search',
      body: 'Type a field name here to filter the outline down to the sections that contain it.',
      placement: 'top',
    },
  ],
}

/**
 * Installing Vera — where scans go, the subdomain, and the embed snippet.
 * Only 3 anchors exist on this page today, so this stays a short tour rather
 * than being padded out.
 */
const installingVera: TourDef = {
  id: 'installing-vera',
  route: ROUTES.SETTINGS_COMPANY_INSTALLATION,
  label: 'Installing Vera on your site',
  description: 'Where scans go, your Vera subdomain, and the embed snippet.',
  category: 'Settings & access',
  scopeAnchor: TOUR_ANCHORS.installationScanDestination,
  steps: [
    {
      id: 'welcome',
      title: 'Getting Vera onto your site',
      body: 'Until a scan destination is set, a scanned tag resolves nowhere useful — this is the first setting worth understanding.',
      placement: 'center',
    },
    {
      id: 'destination',
      anchor: TOUR_ANCHORS.installationScanDestination,
      title: 'Where scans go',
      body: 'Your Vera subdomain, a connected store, or your own site. Changing this later repoints tags already out in the world — even ones already scanned — so choose deliberately.',
      placement: 'bottom',
    },
    {
      id: 'subdomain',
      anchor: TOUR_ANCHORS.installationSubdomain,
      title: 'Your Vera subdomain',
      body: 'Every company gets one by default. Renaming it changes your public address, so it needs a second confirmation on top of Save.',
      placement: 'bottom',
    },
    {
      id: 'widget',
      anchor: TOUR_ANCHORS.installationWidgetSnippet,
      title: 'The embed snippet',
      body: 'If certificates live on your own site, this is the code your developer drops in to verify and link back to Vera.',
      placement: 'top',
    },
  ],
}

/**
 * Billing & activations — the two pages are functionally one flow, connected
 * only by an easy-to-miss link (see the VRG-228 candidate-tour notes). The
 * final step's `branches` hand-off into `activationTriggers` is what actually
 * closes that gap, since the two pages can't share a `scopeAnchor`.
 */
const billingActivations: TourDef = {
  id: 'billing-and-activations',
  route: ROUTES.SETTINGS_COMPANY_BILLING,
  label: 'Billing & activations',
  description: 'What makes a certificate billable, and where to manage payment.',
  category: 'Settings & access',
  scopeAnchor: TOUR_ANCHORS.billingOverview,
  steps: [
    {
      id: 'welcome',
      title: 'Billing & activations',
      body: 'Two connected pages: what counts as a billable activation, and where you pay for it.',
      placement: 'center',
    },
    {
      id: 'overview',
      anchor: TOUR_ANCHORS.billingOverview,
      title: 'Your plan at a glance',
      body: "Subscription, next invoice, and how many activations you've used this period.",
      placement: 'top',
    },
    {
      id: 'payment',
      anchor: TOUR_ANCHORS.billingAddPaymentMethod,
      title: 'Payment methods',
      body: 'Add or update the card on file here.',
      placement: 'bottom',
    },
    {
      id: 'trigger',
      title: 'What actually triggers billing',
      body: "An activation happens when a certificate crosses its trigger — set on the Features page, not here. That's the one link between these two screens, and it's easy to miss.",
      placement: 'center',
      branches: [{ label: 'See activation triggers', tourId: 'activation-triggers' }],
    },
  ],
}

/**
 * Satellite of `billingActivations` — the actual trigger picker, on the
 * Features page. Kept separate because `billingActivations` is scoped to
 * Billing and this anchor lives on a different route.
 */
const activationTriggers: TourDef = {
  id: 'activation-triggers',
  route: ROUTES.SETTINGS_COMPANY_FEATURES,
  label: 'Certificate activation triggers',
  description: 'When a certificate counts as activated — and becomes billable.',
  category: 'Settings & access',
  scopeAnchor: TOUR_ANCHORS.featuresActivationTriggers,
  steps: [
    {
      id: 'welcome',
      title: 'When does a certificate activate?',
      body: 'One setting decides it, and it decides your bill too.',
      placement: 'center',
    },
    {
      id: 'triggers',
      anchor: TOUR_ANCHORS.featuresActivationTriggers,
      title: 'The trigger options',
      // VRG-528 — was "first scan, first transfer, or manually", which named none of
      // the three options that actually exist. Stale since the triggers were renamed;
      // caught by the §56 drift warning on the commit that changed this surface.
      body: 'Pick when a certificate counts as activated — on creation, on sale, or when one of your locations confirms it arrived. You can override this per product and per certificate. Changing it needs confirmation, since it changes what becomes billable going forward.',
      placement: 'top',
    },
  ],
}

/**
 * Roles & permissions — the matrix itself. Assigning users to a role lives on
 * a per-role page with no stable route to link to (each role has its own id,
 * so a `cta.href` can't target one generically) — the final step's `branches`
 * hands off to `assigningRoleUsers` instead, which is scoped to the anchor
 * rather than a specific route.
 */
const rolesPermissions: TourDef = {
  id: 'roles-and-permissions',
  route: ROUTES.SETTINGS_COMPANY_ROLES,
  label: 'Roles & permissions',
  description: 'The permission matrix, and assigning users to a role.',
  category: 'Settings & access',
  scopeAnchor: TOUR_ANCHORS.rolesMatrix,
  steps: [
    {
      id: 'welcome',
      title: 'Roles & permissions',
      body: 'Who can do what, in one grid.',
      placement: 'center',
    },
    {
      id: 'matrix',
      anchor: TOUR_ANCHORS.rolesMatrix,
      title: 'The permission matrix',
      body: 'Each row is a capability, each column a role — toggle a cell to grant or revoke it for that role.',
      placement: 'top',
    },
    {
      id: 'assign',
      title: 'Assigning users',
      body: 'Open any individual role from this list to add or remove who holds it.',
      placement: 'center',
      branches: [{ label: 'Assigning users to a role', tourId: 'assigning-role-users' }],
    },
  ],
}

/**
 * Satellite of `rolesPermissions` — "Assign users" on a single role's own
 * detail page. Kept separate because the anchor lives on a per-role route
 * (`RolePage`), not on the roles LIST page `rolesPermissions` is scoped to.
 */
const assigningRoleUsers: TourDef = {
  id: 'assigning-role-users',
  label: 'Assigning users to a role',
  description: 'Adding or removing who holds a role.',
  category: 'Settings & access',
  scopeAnchor: TOUR_ANCHORS.roleAssignUsers,
  steps: [
    {
      id: 'welcome',
      title: "A role's own page",
      body: 'Open any role from the list to reach this.',
      placement: 'center',
    },
    {
      id: 'assign',
      anchor: TOUR_ANCHORS.roleAssignUsers,
      title: 'Assign users',
      body: 'Add or remove the people who hold this role.',
      placement: 'bottom',
    },
  ],
}

/**
 * Settings orientation — a light hub tour across the sub-nav. Deliberately
 * short: it's a map, not a walkthrough of every section.
 *
 * ⚠️ DRIFT (§56, found by VRG-227, not fixed here — TourStep has no conditional/
 * dynamic body, so a seat-aware rewrite is a real feature, not a copy edit).
 * `nav`'s body ("One list, two halves" / "Personal vs. Company" / naming "users,
 * roles, billing") describes the NORMAL two-section nav. On a single-seat plan
 * (Starter, Individual) Settings collapses to one list with no Personal/Company
 * split, and Users/Roles/Activity log are hidden — so for those companies this
 * step now narrates a structure that isn't on screen. Needs either a seat-aware
 * step body or a seat-aware variant of this tour before it's accurate for every
 * plan. File a follow-up under the tours epic (VRG-228) if picking this up.
 */
const settingsOrientation: TourDef = {
  id: 'settings-orientation',
  route: ROUTES.SETTINGS,
  label: 'Settings orientation',
  description: 'A quick map of what lives under Settings.',
  category: 'Settings & access',
  scopeAnchor: TOUR_ANCHORS.settingsNav,
  steps: [
    {
      id: 'welcome',
      title: 'Finding your way around Settings',
      body: 'One list, two halves.',
      placement: 'center',
    },
    {
      id: 'nav',
      anchor: TOUR_ANCHORS.settingsNav,
      title: 'Personal vs. Company',
      body: 'Personal settings are about you — profile, appearance, notifications. Company settings are about your brand — installation, users, roles, billing, and more.',
      placement: 'right',
    },
    {
      id: 'locked',
      anchor: TOUR_ANCHORS.settingsNav,
      title: 'A greyed-out row',
      body: "That means a permission you don't hold, not a missing feature — hover it to see which one, and who to ask.",
      placement: 'right',
    },
  ],
}

/**
 * People & owners — disambiguates Owners (customer ownership records) from
 * Users (staff accounts under Settings), a real naming-confusion risk called
 * out in the VRG-228 candidate-tour notes.
 */
const peopleAndOwners: TourDef = {
  id: 'people-and-owners',
  route: ROUTES.OWNERS,
  label: 'People & owners',
  description: 'Owners vs. users — and how ownership records work.',
  category: 'People & owners',
  scopeAnchor: TOUR_ANCHORS.peopleTable,
  steps: [
    {
      id: 'welcome',
      title: 'People & owners',
      body: 'Two lists sound alike — this is the one for your customers, not your staff.',
      placement: 'center',
    },
    {
      id: 'table',
      anchor: TOUR_ANCHORS.peopleTable,
      title: 'Owners, not staff',
      body: 'Everyone here is a customer who owns, or has owned, a certificate — not someone with a login to your Settings. Staff accounts live under Settings → Users instead.',
      placement: 'top',
    },
    {
      id: 'detail',
      anchor: TOUR_ANCHORS.ownerDetail,
      title: "An owner's record",
      body: "Every certificate this person currently owns or has owned, in one place — the other side of a certificate's own Provenance tab.",
      placement: 'top',
    },
  ],
}

/**
 * Styling your Vera installation — Studio's brand theme, logo, and the
 * Certificate/Email template split. The welcome step directly addresses the
 * AppearancePage mix-up flagged in the VRG-228 candidate-tour notes: Studio is
 * brand styling, AppearancePage (Settings → Personal) is just the current
 * user's own light/dark preference.
 */
const stylingVera: TourDef = {
  id: 'styling-your-installation',
  route: ROUTES.STUDIO,
  label: 'Styling your Vera installation',
  description: 'Brand colors, your logo, and the Certificate vs. Email templates.',
  category: 'Certificates & products',
  scopeAnchor: TOUR_ANCHORS.studioThemeForm,
  steps: [
    {
      id: 'welcome',
      title: 'Studio: your brand, not your own',
      body: "This is where your certificate pages get YOUR BRAND's look — different from the Appearance setting under your personal Settings, which only changes how this admin console looks to you.",
      placement: 'center',
    },
    {
      id: 'theme',
      anchor: TOUR_ANCHORS.studioThemeForm,
      title: 'Colors, fonts, radius',
      body: 'These apply everywhere a certificate page renders — not just in this preview.',
      placement: 'right',
    },
    {
      id: 'logo',
      anchor: TOUR_ANCHORS.studioLogoUpload,
      title: 'Your logo',
      // "Uploaded once" stopped being true when the light/dark toggle landed on this card
      // (§56 — the anchor survived the change, the copy did not). There are four assets:
      // mark and wordmark, each on light and on dark.
      body: 'Pick the mark or the wordmark. Switch to dark to check the versions a reader in dark mode sees.',
      placement: 'right',
    },
    {
      id: 'tabs',
      anchor: TOUR_ANCHORS.studioTemplateTabs,
      title: 'Certificate vs. Email',
      body: "Two separate templates share this page — switch tabs to style the email a certificate's owner receives, not just the page they land on.",
      placement: 'bottom',
    },
    {
      id: 'white-label',
      title: "Not the white-label toggle",
      body: "Hiding \"Powered by Vera\" is a company-wide setting, not a Studio one — the White label card here just mirrors it. The real switch lives on Features.",
      placement: 'center',
      cta: { label: 'Go to Features', href: '/settings/features' },
    },
  ],
}

/**
 * How to help yourself — a hub tour on the Support page that hands off to
 * three tiny satellite tours (browsing guides, the glossary, what's new) via
 * `branches` on its final step, rather than walking all four surfaces
 * linearly. Those three surfaces' anchors don't co-exist with Support's on
 * any one route, so a single linear tour couldn't reach them without
 * fabricating navigation — each is also independently reachable from the
 * tour library/⌘K, or "on this page" once you're actually on their route.
 */
const howToHelpYourself: TourDef = {
  id: 'how-to-help-yourself',
  route: ROUTES.SUPPORT,
  label: 'How to help yourself',
  description: 'Guides, definitions, the changelog, and filing a support request.',
  category: 'Getting started',
  scopeAnchor: TOUR_ANCHORS.supportRequests,
  steps: [
    {
      id: 'welcome',
      title: 'Where to go when you need help',
      body: "Self-service first, then a real request if that doesn't cover it.",
      placement: 'center',
    },
    {
      id: 'new-request',
      anchor: TOUR_ANCHORS.supportNewRequest,
      title: 'Filing a request',
      body: 'One type, one detail field, an optional attachment — kept deliberately minimal.',
      placement: 'bottom',
    },
    {
      id: 'requests',
      anchor: TOUR_ANCHORS.supportRequests,
      title: 'Your requests',
      body: "Every request you've filed and its status, in one place.",
      placement: 'top',
    },
    {
      id: 'more',
      title: 'More ways to help yourself',
      body: 'Guides, a glossary of terms, and a running changelog are also one click from the Help menu.',
      placement: 'center',
      branches: [
        { label: 'Browse guides', tourId: 'browsing-guides' },
        { label: 'Look up a term', tourId: 'glossary' },
        { label: "See what's new", tourId: 'whats-new' },
      ],
    },
  ],
}

/** Satellite of `howToHelpYourself` — the docs reader's own section nav. */
const browsingGuides: TourDef = {
  id: 'browsing-guides',
  route: ROUTES.DOCS,
  label: 'Browsing guides',
  description: 'Finding a step-by-step guide for any part of Vera.',
  category: 'Getting started',
  scopeAnchor: TOUR_ANCHORS.docsNav,
  steps: [
    {
      id: 'welcome',
      title: 'Every guide, organized',
      body: 'A short tour of where guides live.',
      placement: 'center',
    },
    {
      id: 'nav',
      anchor: TOUR_ANCHORS.docsNav,
      title: 'Pick a section',
      body: 'Each one holds a step-by-step guide for that part of Vera.',
      placement: 'right',
    },
  ],
}

/** Satellite of `howToHelpYourself` — the glossary's term list. */
const glossaryTour: TourDef = {
  id: 'glossary',
  route: ROUTES.DOCS_GLOSSARY,
  label: 'Looking up a term',
  description: 'One definition per term, used consistently across the app.',
  category: 'Getting started',
  scopeAnchor: TOUR_ANCHORS.glossaryList,
  steps: [
    {
      id: 'welcome',
      title: 'One definition per term',
      body: "If a word in the app is unfamiliar, it's probably defined here.",
      placement: 'center',
    },
    {
      id: 'list',
      anchor: TOUR_ANCHORS.glossaryList,
      title: 'Search or browse',
      body: 'The same wording shown in tooltips across the app comes from this list.',
      placement: 'top',
    },
  ],
}

/**
 * Satellite of `howToHelpYourself` — the changelog.
 *
 * Its anchor is the NEWEST ENTRY, not the feed (see `TOUR_ANCHORS.whatsNewEntry` for
 * why the feed could not be ringed). Two consequences worth knowing:
 *
 *  - the step copy describes a CARD, because a card is what is ringed. A step written
 *    for a list, pointed at one item, misdescribes what the user is looking at — the
 *    drift §56 is about, and the reason this tour's copy changed with its anchor.
 *  - on an empty archive the anchor is absent, so this tour stops claiming to be "on
 *    this page" and drops to the bottom of the library. It is still launchable from
 *    there, and degrades to two centered cards over a flat dim — which is the honest
 *    presentation for a page with nothing on it yet.
 */
const whatsNewTour: TourDef = {
  id: 'whats-new',
  route: ROUTES.WHATS_NEW,
  label: "What's new",
  description: 'A running changelog of features and fixes as they ship.',
  category: 'Getting started',
  scopeAnchor: TOUR_ANCHORS.whatsNewEntry,
  steps: [
    {
      id: 'welcome',
      title: 'Every release, in order',
      body: "So you don't have to guess what changed.",
      placement: 'center',
    },
    {
      id: 'entry',
      anchor: TOUR_ANCHORS.whatsNewEntry,
      title: 'The newest release',
      body: 'Each card names what shipped and when, newest first. Open one to read the whole note.',
      placement: 'bottom',
    },
  ],
}

/**
 * The closing step EVERY tour ends with, appended by the engine rather than
 * authored per tour.
 *
 * Two reasons it lives here instead of at the end of each tour's `steps`:
 *
 *  1. It is the same step every time — "here is where you replay this" — so
 *     copying it into each tour is copying a thing that must never diverge. The
 *     moment two tours word it differently, the app has two answers to "where do
 *     tours live".
 *  2. It MUST NOT COUNT toward the tour's length. It is chrome, not content, and
 *     counting it made a five-step tour read "6 of 6" and told a user they had one
 *     more thing to learn when they had none. Everything user-facing — the card
 *     counter, "Finish tour · N of M", the library's progress bar — is measured
 *     against `steps.length`, which is now content only, by construction.
 *
 * Its anchor is `sidebarHelp`, which exists on EVERY page. That is exactly why it
 * is excluded from `tourAnchors()` below: were it included, every tour without an
 * explicit `scopeAnchor` would resolve as "on this page" everywhere, which is the
 * failure mode `TourDef.scopeAnchor` documents.
 */
export const TOUR_OUTRO_STEP: TourStep = {
  id: 'help',
  anchor: TOUR_ANCHORS.sidebarHelp,
  title: 'Replay this any time',
  body: 'Tours live under Help, alongside search and support. Click Help to see the list.',
  placement: 'right',
  // No `action`: the point of the step is the gesture. Clicking the real Help row
  // opens the popover AND finishes the tour, so the user ends looking at the place
  // they can restart it.
  awaitAction: {},
}

/**
 * A tour's steps as the ENGINE runs them — authored content plus the shared outro.
 *
 * Use this to play a tour. Use `tour.steps` for anything that counts or measures,
 * which is the whole point of the split.
 */
export function playableSteps(tour: TourDef): TourStep[] {
  return [...tour.steps, TOUR_OUTRO_STEP]
}

/** All tours, in the order they are offered. */
export const TOURS: TourDef[] = [
  askVeraBasics,
  certificatesBasics,
  creatingCertificates,
  certificateGroups,
  certificateDetailTour,
  productsCatalogBasics,
  productEditNavigation,
  stylingVera,
  installingVera,
  billingActivations,
  activationTriggers,
  rolesPermissions,
  assigningRoleUsers,
  settingsOrientation,
  peopleAndOwners,
  howToHelpYourself,
  browsingGuides,
  glossaryTour,
  whatsNewTour,
]

export function getTour(id: string): TourDef | undefined {
  return TOURS.find(t => t.id === id)
}

/** Stable display order for the tour-list category headers. */
export const TOUR_CATEGORY_ORDER: TourCategory[] = [
  'Getting started',
  'Certificates & products',
  'Tags & orders',
  'People & owners',
  'Settings & access',
]

/**
 * Every `data-tour` id a tour references — anchors AND spotlight overrides.
 * Self-maintaining: no route table to drift, and it leans on the same
 * graceful-anchor contract the engine already relies on.
 */
export function tourAnchors(tour: TourDef): string[] {
  const ids = new Set<string>()
  for (const step of tour.steps) {
    if (step.anchor) ids.add(step.anchor)
    if (step.spotlight) ids.add(step.spotlight)
  }
  if (tour.scopeAnchor) ids.add(tour.scopeAnchor)
  return [...ids]
}

/** The anchors that decide whether a tour is "on this page" — its `scopeAnchor`
 *  when it declares one, otherwise every anchor it touches. */
export function tourScopeAnchors(tour: TourDef): string[] {
  return tour.scopeAnchor ? [tour.scopeAnchor] : tourAnchors(tour)
}

/**
 * Is this tour about something currently on screen?
 *
 * Presence of the `data-tour` anchor IS the route check — which is the whole
 * reason a global Help button can be route-aware without anyone maintaining a
 * route→tour table. Delete a page's anchors and its tours stop claiming to be
 * on that page automatically, and `check-tour-anchors.mjs` (first step of the
 * platform build) means those anchors cannot be deleted silently.
 *
 * Must be called while the surface is rendered — it reads the live DOM, so it
 * belongs in an effect fired on open, never in module scope or a render body.
 *
 * The try/catch is not defensive noise: anchor ids come from the registry and
 * flow into a `querySelector` attribute selector, so a malformed id would throw
 * a DOMException and take down whatever is rendering the list.
 */
export function isTourOnPage(tour: TourDef): boolean {
  return tourScopeAnchors(tour).some(anchor => {
    try {
      return Boolean(document.querySelector(`[data-tour="${anchor}"]`))
    } catch {
      return false
    }
  })
}

/**
 * Every tour relevant to what is on screen right now, in registry order.
 *
 * Extracted rather than copied: `ToursDialog` had this loop inline, and the Help
 * popover needed the same answer to offer "Tours on this page". Two hand-rolls of
 * a presence check that must agree with each other is exactly the drift §36 is
 * about — if they disagree, a tour is "on this page" in one surface and missing
 * from the other, with no error anywhere.
 */
export function getOnPageTours(): TourDef[] {
  return TOURS.filter(isTourOnPage)
}
